import { useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { Spinner } from './Spinner';

interface ActionGridProps {
  /** E.164 or display phone for the tel: link. */
  contactPhone: string;
  contactName: string;
  /** Fires when the owner taps "Take over" — pauses Shelby auto-reply. */
  onTakeOver?: () => void;
  /** Whether the take-over mutation is currently in flight. */
  takeOverBusy?: boolean;
  /** Whether Shelby is already paused for this thread (button shows
   *  "Paused" disabled). */
  alreadyPaused?: boolean;
  /** Fires when the owner confirms "Send review request". */
  onReviewRequest: () => void;
  /** Whether the review-request mutation is currently in flight. */
  reviewBusy?: boolean;
  /** Fires when the owner taps "Mark booked". Slot 3 is contextual —
   *  Book when the thread isn't booked yet, Review when it is. */
  onMarkBooked?: () => void;
  /** Whether the mark-booked mutation is currently in flight. */
  markBookedBusy?: boolean;
  /** True when the thread is already booked. Slot 3 swaps from Book →
   *  Review, and a secondary "Unbook" chip surfaces below the action grid
   *  so the owner can undo a booking after the post-action toast is gone
   *  (Ernie 2026-05-24 — "i need to be able to unbook a convo"). */
  alreadyBooked?: boolean;
  /** Fires when the owner taps the "Unbook" secondary chip on an
   *  already-booked thread. Hits POST /pwa/conversations/:id/unmark-booked
   *  via useUnmarkBooked. Omit and the chip stays hidden. */
  onUnmarkBooked?: () => void;
  /** Whether the unmark-booked mutation is currently in flight. */
  unmarkBookedBusy?: boolean;
  /**
   * Optional Chunk-5 "Mark resolved" handler. When provided, the grid
   * renders a 4-column variant with the resolve button as the last column.
   * Omit to keep the original 3-button layout.
   */
  onMarkResolved?: () => void;
  /** Optional inverse — fires when the user taps "Unarchive". When omitted
   *  and the thread is already archived, the button stays disabled. */
  onUnarchive?: () => void;
  /** Whether the mark-resolved mutation is currently in flight. */
  resolvedBusy?: boolean;
  /** Whether the unarchive mutation is currently in flight. */
  unarchiveBusy?: boolean;
  /** Whether the conversation is already resolved. When true the button
   *  flips to "Unarchive" (tappable) — Ernie 2026-05-12. */
  alreadyResolved?: boolean;
  /** Fires when the owner taps "Log quote" — opens the manual-quote modal.
   *  Used for off-Shelby quotes where the owner picked up the call himself
   *  (Lori-class scenarios). The grid just opens the modal; the actual
   *  save + recap-trigger logic lives in ConversationDetail. */
  onLogQuote?: () => void;
  /** Whether the log-quote mutation is currently in flight. */
  logQuoteBusy?: boolean;
}

const callIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const takeOverIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const reviewIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const resolvedIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const calendarCheckIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <polyline points="9 16 11 18 15 14" />
  </svg>
);

const dollarTagIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-[14px] w-[14px]">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

/**
 * 3-button grid below the detail header: Call back / Take over (primary) / Review req.
 * Mock parity: .detail-actions (3-col grid, ink-50 cards, primary = ss-blue).
 */
export function ActionGrid({
  contactPhone,
  contactName,
  onTakeOver,
  takeOverBusy = false,
  alreadyPaused = false,
  onReviewRequest,
  reviewBusy = false,
  onMarkBooked,
  markBookedBusy = false,
  alreadyBooked = false,
  onMarkResolved,
  onUnarchive,
  resolvedBusy = false,
  unarchiveBusy = false,
  alreadyResolved = false,
  onLogQuote,
  logQuoteBusy = false,
  onUnmarkBooked,
  unmarkBookedBusy = false,
}: ActionGridProps) {
  const [reviewConfirmOpen, setReviewConfirmOpen] = useState(false);
  const [resolveConfirmOpen, setResolveConfirmOpen] = useState(false);
  const [takeoverConfirmOpen, setTakeoverConfirmOpen] = useState(false);
  const [markBookedConfirmOpen, setMarkBookedConfirmOpen] = useState(false);
  const [unmarkBookedConfirmOpen, setUnmarkBookedConfirmOpen] = useState(false);

  // Tightened padding + minimum-zero text size so 4 labels fit on a
  // narrow iPhone viewport without truncating "Archive". Ernie 2026-05-12.
  const baseBtn =
    'flex flex-col items-center justify-center gap-1 rounded-[10px] border border-ink-200 dark:border-[#334155] bg-ink-50 dark:bg-[#1E293B] px-1 py-2.5 text-[11px] font-semibold text-ink-700 dark:text-[#CBD5E1] transition-all duration-150 ease-ss-standard hover:bg-ink-100 dark:hover:bg-[#334155] active:scale-[0.97] active:bg-ink-200 dark:active:bg-[#475569] disabled:active:scale-100';
  const primaryBtn =
    'flex flex-col items-center justify-center gap-1 rounded-[10px] border border-ss-blue bg-ss-blue px-1 py-2.5 text-[11px] font-semibold text-white transition-all duration-150 ease-ss-standard hover:bg-ss-blue-dark active:scale-[0.97] shadow-ss-sm';

  // 4-button variant when onMarkResolved is provided (Chunk-5).
  const showResolved = typeof onMarkResolved === 'function';
  const gridCols = showResolved ? 'grid-cols-4' : 'grid-cols-3';

  return (
    <>
      <div className={`grid ${gridCols} gap-1.5 border-b border-ink-200 dark:border-[#334155] bg-white dark:bg-[#0F172A] px-3 py-3`}>
        <a
          href={`tel:${contactPhone}`}
          className={baseBtn}
          aria-label={`Call ${contactName} at ${contactPhone}`}
        >
          {callIcon}
          <span className="truncate">Call back</span>
        </a>

        <button
          type="button"
          onClick={() => {
            if (alreadyPaused || takeOverBusy) return;
            setTakeoverConfirmOpen(true);
          }}
          disabled={takeOverBusy || alreadyPaused || typeof onTakeOver !== 'function'}
          className={`${alreadyPaused ? baseBtn : primaryBtn} disabled:opacity-70`}
          aria-label={
            alreadyPaused
              ? 'Shelby is already paused for this thread'
              : 'Pause Shelby and take over this thread'
          }
        >
          {takeOverBusy ? <Spinner size={18} /> : takeOverIcon}
          <span className="truncate">{alreadyPaused ? 'Paused' : 'Take over'}</span>
        </button>

        {/* Slot 3 — contextual. Before the contact is booked, this is
            "Book" (mark them booked). After they're booked, it flips to
            "Review" (send the review request SMS). Ernie 2026-05-13:
            since Square is no longer wired to GHL the owner needs an
            in-PWA way to confirm a booking; review-request only makes
            sense post-job anyway. */}
        {alreadyBooked || typeof onMarkBooked !== 'function' ? (
          <button
            type="button"
            onClick={() => setReviewConfirmOpen(true)}
            disabled={reviewBusy}
            className={`${baseBtn} disabled:opacity-70`}
            aria-label="Send review request"
          >
            {reviewBusy ? <Spinner size={18} /> : reviewIcon}
            <span className="truncate">Review</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setMarkBookedConfirmOpen(true)}
            disabled={markBookedBusy}
            className={`${baseBtn} disabled:opacity-70`}
            aria-label="Mark this contact as booked"
          >
            {markBookedBusy ? <Spinner size={18} /> : calendarCheckIcon}
            <span className="truncate">Book</span>
          </button>
        )}

        {showResolved ? (
          alreadyResolved ? (
            <button
              type="button"
              onClick={() => {
                if (unarchiveBusy || typeof onUnarchive !== 'function') return;
                onUnarchive();
              }}
              disabled={unarchiveBusy || typeof onUnarchive !== 'function'}
              className={`${baseBtn} disabled:opacity-70`}
              aria-label="Restore conversation"
              title="Restore this conversation (removes the pwa_resolved tag)."
            >
              {unarchiveBusy ? <Spinner size={18} /> : resolvedIcon}
              <span className="truncate">Unarchive</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setResolveConfirmOpen(true)}
              disabled={resolvedBusy}
              className={`${baseBtn} disabled:opacity-70`}
              aria-label="Archive conversation"
            >
              {resolvedBusy ? <Spinner size={18} /> : resolvedIcon}
              <span className="truncate">Archive</span>
            </button>
          )
        ) : null}
      </div>

      {/* Secondary action row — "Log quote" for owner-side manual quote
          entry (off-Shelby calls), plus a contextual "Unbook" chip that
          surfaces when the thread is already booked so the owner can undo
          a booking after the post-action toast has auto-dismissed.
          Both live in this same row so the primary 4-column grid above
          stays scannable; we'll move them into an overflow menu if/when
          more secondary actions land. */}
      {typeof onLogQuote === 'function' || (alreadyBooked && typeof onUnmarkBooked === 'function') ? (
        <div className="flex items-center gap-2 border-b border-ink-200 dark:border-[#334155] bg-white dark:bg-[#0F172A] px-3 pb-2.5">
          {typeof onLogQuote === 'function' ? (
            <button
              type="button"
              onClick={() => {
                if (logQuoteBusy) return;
                onLogQuote();
              }}
              disabled={logQuoteBusy}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] border border-ss-blue/40 bg-ss-blue/5 px-2 py-2 text-[12px] font-semibold text-ss-blue transition-all duration-150 ease-ss-standard hover:bg-ss-blue/10 active:scale-[0.99] disabled:opacity-60 dark:border-ss-blue/50 dark:bg-ss-blue/10 dark:text-[#93C5FD] dark:hover:bg-ss-blue/20"
              aria-label="Log a quote you gave on the phone"
            >
              {logQuoteBusy ? <Spinner size={14} /> : dollarTagIcon}
              <span>Log quote I gave</span>
            </button>
          ) : null}
          {alreadyBooked && typeof onUnmarkBooked === 'function' ? (
            <button
              type="button"
              onClick={() => {
                if (unmarkBookedBusy) return;
                setUnmarkBookedConfirmOpen(true);
              }}
              disabled={unmarkBookedBusy}
              className="flex flex-shrink-0 items-center justify-center gap-1.5 rounded-[10px] border border-ink-200 bg-white px-3 py-2 text-[12px] font-semibold text-ink-700 transition-all duration-150 ease-ss-standard hover:bg-ink-50 active:scale-[0.99] disabled:opacity-60 dark:border-[#334155] dark:bg-[#0F172A] dark:text-[#E2E8F0] dark:hover:bg-[#1E293B]"
              aria-label="Undo this booking"
              title="Remove the booked status (undoes Mark Booked)"
            >
              {unmarkBookedBusy ? <Spinner size={14} /> : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-[14px] w-[14px]">
                  <path d="M3 12a9 9 0 1 0 3-6.7" />
                  <polyline points="3 4 3 10 9 10" />
                </svg>
              )}
              <span>Unbook</span>
            </button>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={reviewConfirmOpen}
        title="Send review request?"
        message={`This will text ${contactName} a Google review link from your business number. Only do this after the job is done.`}
        confirmLabel="Send review request"
        cancelLabel="Cancel"
        busy={reviewBusy}
        onCancel={() => setReviewConfirmOpen(false)}
        onConfirm={() => {
          setReviewConfirmOpen(false);
          onReviewRequest();
        }}
      />

      <ConfirmDialog
        open={resolveConfirmOpen}
        title="Archive this conversation?"
        message={`This moves ${contactName}'s thread out of the "Needs you" list. The conversation stays accessible — you can still reply if needed.`}
        confirmLabel="Archive"
        cancelLabel="Cancel"
        busy={resolvedBusy}
        onCancel={() => setResolveConfirmOpen(false)}
        onConfirm={() => {
          setResolveConfirmOpen(false);
          if (onMarkResolved) onMarkResolved();
        }}
      />

      <ConfirmDialog
        open={markBookedConfirmOpen}
        title="Mark as booked?"
        message={`Flip ${contactName}'s thread to "Booked". Use this after they actually confirm the appointment. You can undo immediately if you tap by mistake.`}
        confirmLabel="Mark booked"
        cancelLabel="Cancel"
        busy={markBookedBusy}
        onCancel={() => setMarkBookedConfirmOpen(false)}
        onConfirm={() => {
          setMarkBookedConfirmOpen(false);
          if (onMarkBooked) onMarkBooked();
        }}
      />

      <ConfirmDialog
        open={takeoverConfirmOpen}
        title="Take over this thread?"
        message={`Shelby will stop auto-replying to ${contactName}. New texts from them will sit in this thread until you reply. You can hand it back to Shelby anytime by removing the "shelby_paused" tag on the contact in GHL.`}
        confirmLabel="Take over"
        cancelLabel="Cancel"
        busy={takeOverBusy}
        onCancel={() => setTakeoverConfirmOpen(false)}
        onConfirm={() => {
          setTakeoverConfirmOpen(false);
          if (onTakeOver) onTakeOver();
        }}
      />

      <ConfirmDialog
        open={unmarkBookedConfirmOpen}
        title="Unbook this thread?"
        message={`Remove the booked status from ${contactName}'s thread. Use this if you tapped Mark Booked by mistake, or if the customer cancelled. The thread will flip back to its prior state (Needs you / Quoted).`}
        confirmLabel="Unbook"
        cancelLabel="Cancel"
        busy={unmarkBookedBusy}
        onCancel={() => setUnmarkBookedConfirmOpen(false)}
        onConfirm={() => {
          setUnmarkBookedConfirmOpen(false);
          if (onUnmarkBooked) onUnmarkBooked();
        }}
      />
    </>
  );
}
